import { useEffect, useState } from 'react'

/**
 * Modo de layout do workspace.
 *
 * O projeto tinha 14 media queries em seis breakpoints diferentes (1100, 900,
 * 850, 760, 600, 520 e 440 px) e nenhuma decisão de layout em JavaScript — a
 * adaptação mobile era feita escondendo elementos. Só que trocar a trilha
 * lateral por um dock inferior, ou o inspetor por uma folha, não é esconder:
 * é montar outra árvore. Isso precisa ser decidido aqui.
 *
 * Três modos, três larguras, um lugar só:
 *
 * - `mobile`   (< 760 px): canvas + dock inferior + folha; sem trilha nem inspetor fixos.
 * - `tablet`   (760–1179 px): trilha compacta, inspetor flutuante.
 * - `desktop`  (>= 1180 px): trilha, ribbon e inspetor lado a lado.
 */
export type ModoLayout = 'mobile' | 'tablet' | 'desktop'

export const LARGURA_TABLET = 760
export const LARGURA_DESKTOP = 1180

const CONSULTA_MOBILE = `(max-width: ${LARGURA_TABLET - 1}px)`
const CONSULTA_DESKTOP = `(min-width: ${LARGURA_DESKTOP}px)`

/**
 * O padrão é `desktop`. Em renderização sem `window` — os testes que rodam em
 * Node com `renderToStaticMarkup` — é o único valor que não depende de medir
 * nada, e é o layout mais completo, de forma que nenhum comando some da árvore.
 */
export function modoLayoutAtual(): ModoLayout {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'desktop'
  }
  if (window.matchMedia(CONSULTA_MOBILE).matches) return 'mobile'
  if (window.matchMedia(CONSULTA_DESKTOP).matches) return 'desktop'
  return 'tablet'
}

export function useModoLayout(): ModoLayout {
  const [modo, setModo] = useState<ModoLayout>(modoLayoutAtual)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return

    const consultas = [window.matchMedia(CONSULTA_MOBILE), window.matchMedia(CONSULTA_DESKTOP)]
    const reavaliar = () => setModo(modoLayoutAtual())

    reavaliar()
    for (const consulta of consultas) consulta.addEventListener('change', reavaliar)
    return () => {
      for (const consulta of consultas) consulta.removeEventListener('change', reavaliar)
    }
  }, [])

  return modo
}
